<template>
  <div class="shadow-off d-flex flex-column gap-3" data-testid="sl-wise-materiality">
    <MaterialityFiltersHeader
      data-testid="sl-wise-materiality__filters-header"
      :reportingType.sync="localRuData.reporting_type"
      :periods.sync="localRuData.periods"
      :statementOptions="statementOptions"
      :minDate="minDate"
      :maxDate="maxDate"
      periodSelectorClass="ml-1"
      @removeMonth="$emit('removeMonth', $event)"
      @periods-changed="$emit('periods-changed', $event)"
      :isViewMode="isViewMode"
      :isCommentaryView.sync="localViewCycleType"
      :cycletypeReadview.sync="localViewModuleType"
    />
    <div class="sltable" v-if="!isViewMode" data-testid="sl-wise-materiality__edit-mode-container">
      <lb-datatablenew
        data-testid="sl-wise-materiality__table"
        :headers="glHeaders"
        :items="slFilterData"
        :perpage="itemsPerPage"
        :hidePagination="false"
        :enableslot="[
          'code',
          'name',
          'edit_buttons',
          'datatypes',
          'control_thresholds_amount',
          'control_thresholds_percent',
          'floor_control_thresholds',
          'management_thresholds_amount',
          'management_thresholds_percent',
          'floor_management_thresholds',
          'control_thresholds_amount_regional',
          'control_thresholds_percent_regional',
          'floor_control_thresholds_regional',
          'management_thresholds_amount_regional',
          'management_thresholds_percent_regional',
          'floor_management_thresholds_regional',
        ]"
        :loading="loading"
      >
        <template v-slot:code="{ item }">
          <div class="d-flex align-center">
            <v-divider vertical class="ml-4 mr-0" v-if="item.__child" />
            <div>{{ item.code }} - {{ item.name }}</div>
          </div>
        </template>
        <template v-slot:name="{ item }">
          <div class="d-flex align-center">
            <div class="mr-0">{{ item.name }}</div>
          </div>
        </template>
        <template v-slot:datatypes="">
         <div class="d-flex flex-column gap-5" data-testid="sl-wise-materiality__datatypes-cell">
           <div class="ellipsis-on" data-testid="sl-wise-materiality__datatype-proforma">vs {{ proformaLabel }}</div>
          <div class="ellipsis-on" data-testid="sl-wise-materiality__datatype-forecast">vs {{ forecastLabel }}</div>
          </div>  
        </template>
        <template v-slot:edit_buttons="{ item }">
          <v-col v-if="slLevelEdit.enable && !isViewMode">
            <v-row>
              <v-tooltip bottom content-class="tooltip-bottom" v-if="editableGLs.proforma.indexOf(item._id) === -1">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn data-testid="sl-wise-materiality__edit-proforma-btn" icon v-bind="attrs" v-on="on" color="" @click="$emit('editGL', { id: item._id, type: 'proforma' })" id="edit-proforma-gl">
                    <img 
                  src="/img/icons/PenEditIcon.svg"  
                  alt="Edit-Proforma" 
                />
                  </v-btn>
                </template>
                <span>Edit {{ proformaLabel }} {{ $nova.labelsName('materiality_page','commentary_sl_shortform_label','Drill-Down') }}</span>
              </v-tooltip>
              <v-tooltip bottom content-class="tooltip-bottom" v-else>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn data-testid="sl-wise-materiality__cancel-proforma-btn" icon v-bind="attrs" v-on="on" color="" @click="$emit('cancelEditGL', { id: item._id, type: 'proforma' })" id="cancel-proforma-gl">
                    <v-icon>mdi-close-circle-outline</v-icon>
                  </v-btn>
                </template>
                <span>Cancel</span>
              </v-tooltip>
            </v-row>
            <v-row>
              <v-tooltip bottom content-class="tooltip-bottom" v-if="editableGLs.forecast.indexOf(item._id) === -1">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn data-testid="sl-wise-materiality__edit-forecast-btn" icon v-bind="attrs" v-on="on" color="" @click="$emit('editGL', { id: item._id, type: 'forecast' })" id="edit-forecast-gl">
                    <img 
                  src="/img/icons/PenEditIcon.svg" 
                  alt="Edit-Forecast" 
                />
                  </v-btn>
                </template>
                <span>Edit {{ forecastLabel }} {{ $nova.labelsName('materiality_page','commentary_sl_shortform_label','Drill-Down') }}</span>
              </v-tooltip>
              <v-tooltip bottom content-class="tooltip-bottom" v-else>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn data-testid="sl-wise-materiality__cancel-forecast-btn" icon v-bind="attrs" v-on="on" color="" @click="$emit('cancelEditGL', { id: item._id, type: 'forecast' })" id="cancel-forecast-gl">
                    <v-icon>mdi-close-circle-outline</v-icon>
                  </v-btn>
                </template>
                <span>Cancel</span>
              </v-tooltip>
            </v-row>
          </v-col>
        </template>
        <template v-slot:control_thresholds_amount="{ item }">
          <div class="py-1">
            <lb-number
              data-testid="sl-wise-materiality__control-amount-proforma-input"
              v-if="glData && glData[item._id]"
              v-model="glData[item._id].control_thresholds_amount_proforma"
              min="0"
              hidedetails
              :value="safeGLData(item._id).control_thresholds_amount_proforma || '0'"
              label=""
              :error="safeGLError(item._id).control_thresholds_amount_proforma"
              id="l_c_a_pro"
              class="cell-input"
              :disabled="editableGLs.proforma.indexOf(item._id) === -1"
            />
            <span v-else class="cell-input-placeholder">--</span>
          </div>
          <div class="py-1">
            <lb-number
              data-testid="sl-wise-materiality__control-amount-forecast-input"
              v-if="glData && glData[item._id]"
              v-model="glData[item._id].control_thresholds_amount_forecast"
              min="0"
              hidedetails
              :value="safeGLData(item._id).control_thresholds_amount_forecast || '0'"
              label=""
              :error="safeGLError(item._id).control_thresholds_amount_forecast"
              id="l_c_a_fore"
              class="cell-input"
              :disabled="editableGLs.forecast.indexOf(item._id) === -1"
            />
            <span v-else class="cell-input-placeholder">--</span>
          </div>
        </template>
        <template v-slot:control_thresholds_percent="{ item }">
          <div class="py-1">
            <lb-number
              data-testid="sl-wise-materiality__control-percent-proforma-input"
              v-if="glData && glData[item._id]"
              class="cell-input"
              v-model="glData[item._id].control_thresholds_percent_proforma"
              min="0"
              hidedetails
              :value="safeGLData(item._id).control_thresholds_percent_proforma || '0'"
              label=""
              :error="safeGLError(item._id).control_thresholds_percent_proforma"
              id="l_c_p_pro"
              :disabled="true"
            />
            <span v-else class="cell-input-placeholder">--</span>
          </div>
          <div class="py-1">
            <lb-number
              data-testid="sl-wise-materiality__control-percent-forecast-input"
              v-if="glData && glData[item._id]"
              class="cell-input"
              v-model="glData[item._id].control_thresholds_percent_forecast"
              min="0"
              hidedetails
              :value="safeGLData(item._id).control_thresholds_percent_forecast || '0'"
              label=""
              :error="safeGLError(item._id).control_thresholds_percent_forecast"
              id="l_c_p_fore"
              :disabled="editableGLs.forecast.indexOf(item._id) === -1"
            />
            <span v-else class="cell-input-placeholder">--</span>
          </div>
        </template>
        <template v-slot:management_thresholds_amount="{ item }">
          <div class="py-1">
            <lb-number
              data-testid="sl-wise-materiality__management-amount-proforma-input"
              v-if="glData && glData[item._id]"
              class="cell-input"
              v-model="glData[item._id].management_thresholds_amount_proforma"
              min="0"
              hidedetails
              :value="safeGLData(item._id).management_thresholds_amount_proforma || '0'"
              label=""
              :error="safeGLError(item._id).management_thresholds_amount_proforma"
              id="l_m_a_pro"
              :disabled="editableGLs.proforma.indexOf(item._id) === -1"
            />
            <span v-else class="cell-input-placeholder">--</span>
          </div>
          <div class="py-1">
            <lb-number
              data-testid="sl-wise-materiality__management-amount-forecast-input"
              v-if="glData && glData[item._id]"
              class="cell-input"
              v-model="glData[item._id].management_thresholds_amount_forecast"
              min="0"
              hidedetails
              :value="safeGLData(item._id).management_thresholds_amount_forecast || '0'"
              label=""
              :error="safeGLError(item._id).management_thresholds_amount_forecast"
              id="l_m_a_fore"
              :disabled="editableGLs.forecast.indexOf(item._id) === -1"
            />
            <span v-else class="cell-input-placeholder">--</span>
          </div>
        </template>
        <template v-slot:management_thresholds_percent="{ item }">
          <div class="py-1">
            <lb-number
              data-testid="sl-wise-materiality__management-percent-proforma-input"
              v-if="glData && glData[item._id]"
              class="cell-input"
              v-model="glData[item._id].management_thresholds_percent_proforma"
              min="0"
              hidedetails
              :value="safeGLData(item._id).management_thresholds_percent_proforma || '0'"
              label=""
              :error="safeGLError(item._id).management_thresholds_percent_proforma"
              id="l_m_p_pro"
              :disabled="editableGLs.proforma.indexOf(item._id) === -1"
            />
            <span v-else class="cell-input-placeholder">--</span>
          </div>
          <div class="py-1">
            <lb-number
              data-testid="sl-wise-materiality__management-percent-forecast-input"
              v-if="glData && glData[item._id]"
              class="cell-input"
              v-model="glData[item._id].management_thresholds_percent_forecast"
              min="0"
              hidedetails
              :value="safeGLData(item._id).management_thresholds_percent_forecast || '0'"
              label=""
              :error="safeGLError(item._id).management_thresholds_percent_forecast"
              id="l_m_p_fore"
              :disabled="editableGLs.forecast.indexOf(item._id) === -1"
            />
            <span v-else class="cell-input-placeholder">--</span>
          </div>
        </template>
        <template v-slot:control_thresholds_amount_regional="{ item }">
          <div class="py-1">
            <lb-number
              data-testid="sl-wise-materiality__control-amount-regional-proforma-input"
              v-if="glData && glData[item._id]"
              class="cell-input"
              v-model="glData[item._id].control_thresholds_amount_regional_proforma"
              min="0"
              hidedetails
              :value="safeGLData(item._id).control_thresholds_amount_regional_proforma || '0'"
              label=""
              :error="safeGLError(item._id).control_thresholds_amount_regional_proforma"
              id="r_c_a_pro"
              :disabled="editableGLs.proforma.indexOf(item._id) === -1"
            />
            <span v-else class="cell-input-placeholder">--</span>
          </div>
          <div class="py-1">
            <lb-number
              data-testid="sl-wise-materiality__control-amount-regional-forecast-input"
              v-if="glData && glData[item._id]"
              class="cell-input"
              v-model="glData[item._id].control_thresholds_amount_regional_forecast"
              min="0"
              hidedetails
              :value="safeGLData(item._id).control_thresholds_amount_regional_forecast || '0'"
              label=""
              :error="safeGLError(item._id).control_thresholds_amount_regional_forecast"
              id="r_c_a_fore"
              :disabled="editableGLs.forecast.indexOf(item._id) === -1"
            />
            <span v-else class="cell-input-placeholder">--</span>
          </div>
        </template>
        <template v-slot:control_thresholds_percent_regional="{ item }">
          <div class="py-1">
            <lb-number
              data-testid="sl-wise-materiality__control-percent-regional-proforma-input"
              v-if="glData && glData[item._id]"
              class="cell-input"
              v-model="glData[item._id].control_thresholds_percent_regional_proforma"
              min="0"
              hidedetails
              :value="safeGLData(item._id).control_thresholds_percent_regional_proforma || '0'"
              label=""
              :error="safeGLError(item._id).control_thresholds_percent_regional_proforma"
              id="r_c_p_pro"
              :disabled="true"
            />
            <span v-else class="cell-input-placeholder">--</span>
          </div>
          <div class="py-1">
            <lb-number
              data-testid="sl-wise-materiality__control-percent-regional-forecast-input"
              v-if="glData && glData[item._id]"
              class="cell-input"
              v-model="glData[item._id].control_thresholds_percent_regional_forecast"
              min="0"
              hidedetails
              :value="safeGLData(item._id).control_thresholds_percent_regional_forecast || '0'"
              label=""
              :error="safeGLError(item._id).control_thresholds_percent_regional_forecast"
              id="r_c_p_fore"
              :disabled="editableGLs.forecast.indexOf(item._id) === -1"
            />
            <span v-else class="cell-input-placeholder">--</span>
          </div>
        </template>
        <template v-slot:management_thresholds_amount_regional="{ item }">
          <div class="py-1">
            <lb-number
              data-testid="sl-wise-materiality__management-amount-regional-proforma-input"
              v-if="glData && glData[item._id]"
              class="cell-input"
              v-model="glData[item._id].management_thresholds_amount_regional_proforma"
              min="0"
              hidedetails
              :value="safeGLData(item._id).management_thresholds_amount_regional_proforma || '0'"
              label=""
              :error="safeGLError(item._id).management_thresholds_amount_regional_proforma"
              id="r_m_a_pro"
              :disabled="editableGLs.proforma.indexOf(item._id) === -1"
            />
            <span v-else class="cell-input-placeholder">--</span>
          </div>
          <div class="py-1">
            <lb-number
              data-testid="sl-wise-materiality__management-amount-regional-forecast-input"
              v-if="glData && glData[item._id]"
              class="cell-input"
              v-model="glData[item._id].management_thresholds_amount_regional_forecast"
              min="0"
              hidedetails
              :value="safeGLData(item._id).management_thresholds_amount_regional_forecast || '0'"
              label=""
              :error="safeGLError(item._id).management_thresholds_amount_regional_forecast"
              id="r_m_a_fore"
              :disabled="editableGLs.forecast.indexOf(item._id) === -1"
            />
            <span v-else class="cell-input-placeholder">--</span>
          </div>
        </template>
        <template v-slot:management_thresholds_percent_regional="{ item }">
          <div class="py-1">
            <lb-number
              data-testid="sl-wise-materiality__management-percent-regional-proforma-input"
              v-if="glData && glData[item._id]"
              class="cell-input"
              v-model="glData[item._id].management_thresholds_percent_regional_proforma"
              min="0"
              hidedetails
              :value="safeGLData(item._id).management_thresholds_percent_regional_proforma || '0'"
              label=""
              :error="safeGLError(item._id).management_thresholds_percent_regional_proforma"
              id="r_m_p_pro"
              :disabled="editableGLs.proforma.indexOf(item._id) === -1"
            />
            <span v-else class="cell-input-placeholder">--</span>
          </div>
          <div class="py-1">
            <lb-number
              data-testid="sl-wise-materiality__management-percent-regional-forecast-input"
              v-if="glData && glData[item._id]"
              class="cell-input"
              v-model="glData[item._id].management_thresholds_percent_regional_forecast"
              min="0"
              hidedetails
              :value="safeGLData(item._id).management_thresholds_percent_regional_forecast || '0'"
              label=""
              :error="safeGLError(item._id).management_thresholds_percent_regional_forecast"
              id="r_m_p_fore"
              :disabled="editableGLs.forecast.indexOf(item._id) === -1"
            />
            <span v-else class="cell-input-placeholder">--</span>
          </div>
        </template>
      </lb-datatablenew>
    </div>
    <div v-else data-testid="sl-wise-materiality__view-mode-container">
      <lb-datatablenew
        data-testid="sl-wise-materiality__view-table"
        :headers="filteredGlHeaders"
        :items="viewSLLevelMaterialityData"
        :perpage="itemsPerPage"
        :hidePagination="false"
        :enableslot="[
          'code',
          'name',
          'datatypes',
          'control_thresholds_amount',
          'control_thresholds_percent',
          'floor_control_thresholds',
          'management_thresholds_amount',
          'management_thresholds_percent',
          'floor_management_thresholds',
          'control_thresholds_amount_regional',
          'control_thresholds_percent_regional',
          'floor_control_thresholds_regional',
          'management_thresholds_amount_regional',
          'management_thresholds_percent_regional',
          'floor_management_thresholds_regional',
        ]"
        :loading="loading"
      >
        <template v-slot:code="{ item }">
          <div class="d-flex align-center">
            <v-divider vertical class="ml-4 mr-0" v-if="item.__child" />
            <div>{{ item.code }} - {{ item.name }}</div>
          </div>
        </template>
        <template v-slot:name="{ item }">
          <div class="d-flex align-center">
            <div class="mr-0">{{ item.name }}</div>
          </div>
        </template>
        <template v-slot:datatypes="">
           <div class="d-flex flex-column gap-5" data-testid="sl-wise-materiality__datatypes-cell">
           <div class="ellipsis-on" data-testid="sl-wise-materiality__datatype-proforma">vs {{ proformaLabel }}</div>
          <div class="ellipsis-on" data-testid="sl-wise-materiality__datatype-forecast">vs {{ forecastLabel }}</div>
          </div>  
        </template>
        <template v-slot:control_thresholds_amount="{ item }">
          <div class="cell-input">
            <lb-number
              data-testid="sl-wise-materiality__view-control-amount-proforma-input"
              v-if="editableGLs.proforma.indexOf(item._id) > -1"
              v-model="item.control_thresholds_amount_proforma"
              min="0"
              hidedetails
              :value="item.control_thresholds_amount_proforma || '0'"
              label=""
              :error="glError[item._id].control_thresholds_amount_proforma"
              id="l_c_a_pro"
            />
            <div class="text-right" id="l_c_a_pro" v-else data-testid="sl-wise-materiality__view-control-amount-proforma-value">
              <span v-if="item.control_thresholds_amount_proforma !== 0.001">{{ item.control_thresholds_amount_proforma || 0 }}</span>
              <span v-else> - </span>
            </div>
          </div>
          <div class="cell-input">
            <lb-number
              data-testid="sl-wise-materiality__view-control-amount-forecast-input"
              v-if="editableGLs.forecast.indexOf(item._id) > -1"
              v-model="item.control_thresholds_amount_forecast"
              min="0"
              hidedetails
              :value="item.control_thresholds_amount_forecast || '0'"
              label=""
              :error="glError[item._id].control_thresholds_amount_forecast"
              id="l_c_a_fore"
            />
            <div class="text-right" id="l_c_a_fore" v-else data-testid="sl-wise-materiality__view-control-amount-forecast-value">
              <span v-if="item.control_thresholds_amount_forecast !== 0.001">{{ item.control_thresholds_amount_forecast || 0 }}</span>
              <span v-else> - </span>
            </div>
          </div>
        </template>
        <template v-slot:control_thresholds_percent="{ item }">
          <div class="cell-input">
            <lb-number
              data-testid="sl-wise-materiality__view-control-percent-proforma-input"
              v-if="editableGLs.proforma.indexOf(item._id) > -1"
              v-model="item.control_thresholds_percent_proforma"
              min="0"
              hidedetails
              :value="item.control_thresholds_percent_proforma || '0'"
              label=""
              :error="glError[item._id].control_thresholds_percent_proforma"
              id="l_c_p_pro"
              :disabled="true"
            />
            <div class="text-right" id="l_c_p_pro" v-else data-testid="sl-wise-materiality__view-control-percent-proforma-value">
              <span v-if="item.control_thresholds_percent_proforma !== 0.001">{{ item.control_thresholds_percent_proforma || 0 }}%</span>
              <span v-else> - </span>
            </div>
          </div>
          <div class="cell-input">
            <lb-number
              data-testid="sl-wise-materiality__view-control-percent-forecast-input"
              v-if="editableGLs.forecast.indexOf(item._id) > -1"
              v-model="item.control_thresholds_percent_forecast"
              min="0"
              hidedetails
              :value="item.control_thresholds_percent_forecast || '0'"
              label=""
              :error="glError[item._id].control_thresholds_percent_forecast"
              id="l_c_p_fore"
            />
            <div class="text-right" id="l_c_p_fore" v-else data-testid="sl-wise-materiality__view-control-percent-forecast-value">
              <span v-if="item.control_thresholds_percent_forecast !== 0.001">{{ item.control_thresholds_percent_forecast || 0 }}%</span>
              <span v-else> - </span>
            </div>
          </div>
        </template>
        <template v-slot:management_thresholds_amount="{ item }">
          <div class="cell-input">
            <lb-number
              data-testid="sl-wise-materiality__view-management-amount-proforma-input"
              v-if="editableGLs.proforma.indexOf(item._id) > -1"
              v-model="item.management_thresholds_amount_proforma"
              min="0"
              hidedetails
              :value="item.management_thresholds_amount_proforma || '0'"
              label=""
              :error="glError[item._id].management_thresholds_amount_proforma"
              id="l_m_a_pro"
            />
            <div class="text-right" id="l_m_a_pro" v-else data-testid="sl-wise-materiality__view-management-amount-proforma-value">
              <span v-if="item.management_thresholds_amount_proforma !== 0.001">{{ item.management_thresholds_amount_proforma || 0 }}</span>
              <span v-else> - </span>
            </div>
          </div>
          <div class="cell-input">
            <lb-number
              data-testid="sl-wise-materiality__view-management-amount-forecast-input"
              v-if="editableGLs.forecast.indexOf(item._id) > -1"
              v-model="item.management_thresholds_amount_forecast"
              min="0"
              hidedetails
              :value="item.management_thresholds_amount_forecast || '0'"
              label=""
              :error="glError[item._id].management_thresholds_amount_forecast"
              id="l_m_a_fore"
            />
            <div class="text-right" id="l_m_a_fore" v-else data-testid="sl-wise-materiality__view-management-amount-forecast-value">
              <span v-if="item.management_thresholds_amount_forecast !== 0.001">{{ item.management_thresholds_amount_forecast || 0 }}</span>
              <span v-else> - </span>
            </div>
          </div>
        </template>
        <template v-slot:management_thresholds_percent="{ item }">
          <div class="cell-input">
            <lb-number
              data-testid="sl-wise-materiality__view-management-percent-proforma-input"
              v-if="editableGLs.proforma.indexOf(item._id) > -1"
              v-model="item.management_thresholds_percent_proforma"
              min="0"
              hidedetails
              :value="item.management_thresholds_percent_proforma || '0'"
              label=""
              :error="glError[item._id].management_thresholds_percent_proforma"
              id="l_m_p_pro"
            />
            <div class="text-right" v-else data-testid="sl-wise-materiality__view-management-percent-proforma-value">
              <span v-if="item.management_thresholds_percent_proforma !== 0.001">{{ item.management_thresholds_percent_proforma || 0 }}%</span>
              <span v-else> - </span>
            </div>
          </div>
          <div class="cell-input">
            <lb-number
              data-testid="sl-wise-materiality__view-management-percent-forecast-input"
              v-if="editableGLs.forecast.indexOf(item._id) > -1"
              v-model="item.management_thresholds_percent_forecast"
              min="0"
              hidedetails
              :value="item.management_thresholds_percent_forecast || '0'"
              label=""
              :error="glError[item._id].management_thresholds_percent_forecast"
              id="l_m_p_fore"
            />
            <div class="text-right" v-else data-testid="sl-wise-materiality__view-management-percent-forecast-value">
              <span v-if="item.management_thresholds_percent_forecast !== 0.001">{{ item.management_thresholds_percent_forecast || 0 }}%</span>
              <span v-else> - </span>
            </div>
          </div>
        </template>
        <template v-slot:control_thresholds_amount_regional="{ item }">
          <div class="cell-input">
            <lb-number
              data-testid="sl-wise-materiality__view-control-amount-regional-proforma-input"
              v-if="editableGLs.proforma.indexOf(item._id) > -1"
              v-model="item.control_thresholds_amount_regional_proforma"
              min="0"
              hidedetails
              :value="item.control_thresholds_amount_regional_proforma || '0'"
              label=""
              :error="glError[item._id].control_thresholds_amount_regional_proforma"
              id="r_c_a_pro"
            />
            <div class="text-right" id="r_c_a_pro" v-else data-testid="sl-wise-materiality__view-control-amount-regional-proforma-value">
              <span v-if="item.control_thresholds_amount_regional_proforma !== 0.001">{{ item.control_thresholds_amount_regional_proforma || 0 }}</span>
              <span v-else> - </span>
            </div>
          </div>
          <div class="cell-input">
            <lb-number
              data-testid="sl-wise-materiality__view-control-amount-regional-forecast-input"
              v-if="editableGLs.forecast.indexOf(item._id) > -1"
              v-model="item.control_thresholds_amount_regional_forecast"
              min="0"
              hidedetails
              :value="item.control_thresholds_amount_regional_forecast || '0'"
              label=""
              :error="glError[item._id].control_thresholds_amount_regional_forecast"
              id="r_c_a_fore"
            />
            <div class="text-right" id="r_c_a_fore" v-else data-testid="sl-wise-materiality__view-control-amount-regional-forecast-value">
              <span v-if="item.control_thresholds_amount_regional_forecast !== 0.001">{{ item.control_thresholds_amount_regional_forecast || 0 }}</span>
              <span v-else> - </span>
            </div>
          </div>
        </template>
        <template v-slot:control_thresholds_percent_regional="{ item }">
          <div class="cell-input">
            <lb-number
              data-testid="sl-wise-materiality__view-control-percent-regional-proforma-input"
              v-if="editableGLs.proforma.indexOf(item._id) > -1"
              v-model="item.control_thresholds_percent_regional_proforma"
              min="0"
              hidedetails
              :value="item.control_thresholds_percent_regional_proforma || '0'"
              label=""
              :error="glError[item._id].control_thresholds_percent_regional_proforma"
              id="r_c_p_pro"
              :disabled="true"
            />
            <div class="text-right" id="r_c_p_pro" v-else data-testid="sl-wise-materiality__view-control-percent-regional-proforma-value">
              <span v-if="item.control_thresholds_percent_regional_proforma !== 0.001">{{ item.control_thresholds_percent_regional_proforma || 0 }}%</span>
              <span v-else> - </span>
            </div>
          </div>
          <div class="cell-input">
            <lb-number
              data-testid="sl-wise-materiality__view-control-percent-regional-forecast-input"
              v-if="editableGLs.forecast.indexOf(item._id) > -1"
              v-model="item.control_thresholds_percent_regional_forecast"
              min="0"
              hidedetails
              :value="item.control_thresholds_percent_regional_forecast || '0'"
              label=""
              :error="glError[item._id].control_thresholds_percent_regional_forecast"
              id="r_c_p_fore"
            />
            <div class="text-right" id="r_c_p_fore" v-else data-testid="sl-wise-materiality__view-control-percent-regional-forecast-value">
              <span v-if="item.control_thresholds_percent_regional_forecast !== 0.001">{{ item.control_thresholds_percent_regional_forecast || 0 }}%</span>
              <span v-else> - </span>
            </div>
          </div>
        </template>
        <template v-slot:management_thresholds_amount_regional="{ item }">
          <div class="cell-input">
            <lb-number
              data-testid="sl-wise-materiality__view-management-amount-regional-proforma-input"
              v-if="editableGLs.proforma.indexOf(item._id) > -1"
              v-model="item.management_thresholds_amount_regional_proforma"
              min="0"
              hidedetails
              :value="item.management_thresholds_amount_regional_proforma || '0'"
              label=""
              :error="glError[item._id].management_thresholds_amount_regional_proforma"
              id="r_m_a_pro"
            />
            <div class="text-right" id="r_m_a_pro" v-else data-testid="sl-wise-materiality__view-management-amount-regional-proforma-value">
              <span v-if="item.management_thresholds_amount_regional_proforma !== 0.001">{{ item.management_thresholds_amount_regional_proforma || 0 }}</span>
              <span v-else> - </span>
            </div>
          </div>
          <div class="cell-input">
            <lb-number
              data-testid="sl-wise-materiality__view-management-amount-regional-forecast-input"
              v-if="editableGLs.forecast.indexOf(item._id) > -1"
              v-model="item.management_thresholds_amount_regional_forecast"
              min="0"
              hidedetails
              :value="item.management_thresholds_amount_regional_forecast || '0'"
              label=""
              :error="glError[item._id].management_thresholds_amount_regional_forecast"
              id="r_m_a_fore"
            />
            <div class="text-right" id="r_m_a_fore" v-else data-testid="sl-wise-materiality__view-management-amount-regional-forecast-value">
              <span v-if="item.management_thresholds_amount_regional_forecast !== 0.001">{{ item.management_thresholds_amount_regional_forecast || 0 }}</span>
              <span v-else> - </span>
            </div>
          </div>
        </template>
        <template v-slot:management_thresholds_percent_regional="{ item }">
          <div class="cell-input">
            <lb-number
              data-testid="sl-wise-materiality__view-management-percent-regional-proforma-input"
              v-if="editableGLs.proforma.indexOf(item._id) > -1"
              v-model="item.management_thresholds_percent_regional_proforma"
              min="0"
              hidedetails
              :value="item.management_thresholds_percent_regional_proforma || '0'"
              label=""
              :error="glError[item._id].management_thresholds_percent_regional_proforma"
              id="r_m_p_pro"
            />
            <div class="text-right" id="r_m_p_pro" v-else data-testid="sl-wise-materiality__view-management-percent-regional-proforma-value">
              <span v-if="item.management_thresholds_percent_regional_proforma !== 0.001">{{ item.management_thresholds_percent_regional_proforma || 0 }}%</span>
              <span v-else> - </span>
            </div>
          </div>
          <div class="cell-input">
            <lb-number
              data-testid="sl-wise-materiality__view-management-percent-regional-forecast-input"
              v-if="editableGLs.forecast.indexOf(item._id) > -1"
              v-model="item.management_thresholds_percent_regional_forecast"
              min="0"
              hidedetails
              :value="item.management_thresholds_percent_regional_forecast || '0'"
              label=""
              :error="glError[item._id].management_thresholds_percent_regional_forecast"
              id="r_m_p_fore"
            />
            <div class="text-right" id="r_m_p_fore" v-else data-testid="sl-wise-materiality__view-management-percent-regional-forecast-value">
              <span v-if="item.management_thresholds_percent_regional_forecast !== 0.001">{{ item.management_thresholds_percent_regional_forecast || 0 }}%</span>
              <span v-else> - </span>
            </div>
          </div>
        </template>
      </lb-datatablenew>
    </div>
  </div>
</template>

<script>

import MaterialityFiltersHeader from './MaterialityFiltersHeader.vue';

export default {
  name: 'SLWiseMateriality',
  components: {
    MaterialityFiltersHeader,
  },
  data() {
    return {
      selectedStatementType: 'pl',
      statementOptions: [{labelName: 'Profit & Loss',name: 'pl'}, {labelName: 'Balance Sheet', name: 'bs'}],
      itemsPerPage: 10,
    };
  },
  props: {
    ruData: {
      type: Object,
      default: () => ({}),
    },
    glHeaders: {
      type: Array,
      default: () => [],
    },
    slFilterData: {
      type: Array,
      default: () => [],
    },
    glData: {
      type: Object,
      default: () => ({}),
    },
    glError: {
      type: Object,
      default: () => ({}),
    },
    editableGLs: {
      type: Object,
      default: () => ({ proforma: [], forecast: [] }),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    minDate: {
      type: String,
      default: '',
    },
    maxDate: {
      type: String,
      default: '',
    },
    multipleMaterialityToggle: {
      type: Boolean,
      default: false,
    },
    slLevelEdit: {
      type: Object,
      default: () => ({ enable: false }),
    },
    isViewMode: {
      type: Boolean,
      default: false,
    },
    viewSLLevelMaterialityData: {
      type: Array,
      default: () => [],
    },
    // View mode filter state props (to preserve user selections)
    viewCycleType: {
      type: String,
      default: 'actual'
    },
    viewModuleType: {
      type: String,
      default: 'pl'
    },
  },
  computed: {
    localRuData: {
      get() {
        return this.ruData;
      },
      set(val) {
        this.$emit('update:ruData', val);
      },
    },
    /**
     * Local view cycle type with two-way binding to parent
     */
    localViewCycleType: {
      get() {
        return this.viewCycleType;
      },
      set(val) {
        this.$emit('update:viewCycleType', val);
      },
    },
    /**
     * Local view module type with two-way binding to parent
     */
    localViewModuleType: {
      get() {
        return this.viewModuleType;
      },
      set(val) {
        this.$emit('update:viewModuleType', val);
      },
    },
    /**
     * Safely access GL data with default values to prevent undefined errors
     * @param {string} itemId - The GL item ID
     * @returns {object} GL data object with all threshold properties
     */
    safeGLData() {
      return (itemId) => {
        if (!this.glData || !itemId || !this.glData[itemId]) {
          // Return default structure if data is missing
          return {
            control_thresholds_amount_proforma: 0,
            control_thresholds_percent_proforma: 0,
            management_thresholds_amount_proforma: 0,
            management_thresholds_percent_proforma: 0,
            control_thresholds_amount_regional_proforma: 0,
            control_thresholds_percent_regional_proforma: 0,
            management_thresholds_amount_regional_proforma: 0,
            management_thresholds_percent_regional_proforma: 0,
            control_thresholds_amount_forecast: 0,
            control_thresholds_percent_forecast: 0,
            management_thresholds_amount_forecast: 0,
            management_thresholds_percent_forecast: 0,
            control_thresholds_amount_regional_forecast: 0,
            control_thresholds_percent_regional_forecast: 0,
            management_thresholds_amount_regional_forecast: 0,
            management_thresholds_percent_regional_forecast: 0,
          };
        }
        return this.glData[itemId];
      };
    },
    /**
     * Safely access GL error data with default values
     * @param {string} itemId - The GL item ID
     * @returns {object} GL error object
     */
    safeGLError() {
      return (itemId) => {
        if (!this.glError || !itemId || !this.glError[itemId]) {
          return {};
        }
        return this.glError[itemId];
      };
    },
    /**
     * Filter headers to hide edit_buttons column in view mode
     */
    filteredGlHeaders() {
      if (this.isViewMode) {
        return this.glHeaders.filter(header => header.value !== 'edit_buttons');
      }
      return this.glHeaders;
    },
    /**
     * Dynamic label for Actual based on reporting_type
     * Returns balance sheet specific label when type is 'bs', otherwise default
     */
    actualLabel() {
      return this.localRuData.reporting_type === 'bs'
        ? this.$nova.labelsName('cycle_page', 'actual_label_balancesheet', 'Actual')
        : this.$nova.labelsName('cycle_page', 'actual_label', 'Actual');
    },
    /**
     * Dynamic label for Proforma based on reporting_type
     * Returns balance sheet specific label when type is 'bs', otherwise default
     */
    proformaLabel() {
      return this.localRuData.reporting_type === 'bs'
        ? this.$nova.labelsName('cycle_page', 'proforma_shortform_label_balancesheet', 'Proforma')
        : this.$nova.labelsName('cycle_page', 'proforma_shortform_label', 'Proforma');
    },
    /**
     * Dynamic label for Forecast based on reporting_type
     * Returns balance sheet specific label when type is 'bs', otherwise default
     */
    forecastLabel() {
      return this.localRuData.reporting_type === 'bs'
        ? this.$nova.labelsName('cycle_page', 'forecast_shortform_label_balancesheet', 'Forecast')
        : this.$nova.labelsName('cycle_page', 'forecast_shortform_label', 'Forecast');
    },
  },
  watch: {
    /**
     * Watch for changes in slFilterData to initialize data structures
     */
    slFilterData: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.initializeGLData(newVal);
        }
      },
      immediate: true,
      deep: true,
    },
    /**
     * Watch for changes in editableGLs to initialize data structures
     */
    'editableGLs.proforma': {
      handler(newVal) {
        newVal.forEach(itemId => {
          if (!this.glData[itemId]) {
            this.initializeItemData(itemId);
          }
        });
      },
      deep: true,
    },
    'editableGLs.forecast': {
      handler(newVal) {
        newVal.forEach(itemId => {
          if (!this.glData[itemId]) {
            this.initializeItemData(itemId);
          }
        });
      },
      deep: true,
    },
    'localRuData.periods'(newVal, oldVal) {
      if (this.isViewMode) {
        if (newVal && newVal.length > 0 && JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
          this.$emit('periods-changed', newVal);
        }
      }
    },
  },
  methods: {
    /**
     * Initialize GL data for all items in slFilterData
     * @param {Array} items - Array of SL items
     */
    initializeGLData(items) {
      items.forEach(item => {
        if (item._id && !this.glData[item._id]) {
          this.initializeItemData(item._id);
        }
      });
    },
    /**
     * Initialize data structure for a single item
     * @param {string} itemId - The item ID
     */
    initializeItemData(itemId) {
      this.$set(this.glData, itemId, {
        control_thresholds_amount_proforma: 0.001,
        control_thresholds_percent_proforma: 999999,
        management_thresholds_amount_proforma: 0.001,
        management_thresholds_percent_proforma: 0.001,
        control_thresholds_amount_regional_proforma: 0.001,
        control_thresholds_percent_regional_proforma: 999999,
        management_thresholds_amount_regional_proforma: 0.001,
        management_thresholds_percent_regional_proforma: 0.001,
        control_thresholds_amount_forecast: 0.001,
        control_thresholds_percent_forecast: 0.001,
        management_thresholds_amount_forecast: 0.001,
        management_thresholds_percent_forecast: 0.001,
        control_thresholds_amount_regional_forecast: 0.001,
        control_thresholds_percent_regional_forecast: 0.001,
        management_thresholds_amount_regional_forecast: 0.001,
        management_thresholds_percent_regional_forecast: 0.001,
      });
    },
  },
  mounted() {
    console.log('[SLWiseMateriality]: Component mounted', {
      slFilterDataCount: this.slFilterData?.length || 0,
      glHeadersCount: this.glHeaders?.length || 0,
      glDataKeys: Object.keys(this.glData || {}).length,
      editableGLs: this.editableGLs,
    });
    
    // Initialize GL data for all items on mount
    if (this.slFilterData && this.slFilterData.length > 0) {
      this.initializeGLData(this.slFilterData);
    }
  },
};
</script>

<style scoped>
.ellipsis-on {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.icon-size-16 {
  width: 16px;
  height: 16px;
}
.v-table-color-materiality  tr td {
  padding: 0px 4px !important;
}
.cell-input-placeholder {
  display: block;
  text-align: center;
  color: #999;
  font-size: 12px;
  padding: 8px 0;
}
.chip-gray-close >>> .v-chip__close {
  color: #828285 !important;
}
.pageination-theme >>> .v-pagination__item {
  background-color: var(--grey-50);
}
</style>
